import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Alert, Box, Chip, CircularProgress, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { MonitoredWarningExtractionModal } from '@old/features/silobags/silobag-detail/exctraction-modal/monitored-warning-extraction-modal';
import {
  closeSilobagsService,
  type CloseSilobagsResponse,
} from '@siloreal:shared:api/service/silobag/silobag-close';
import { listSilobagsService, type SilobagListItem } from '@siloreal:shared:api/service/silobag/silobag-list';
import { listMonitoredSilobagProtocolsService } from '@siloreal:shared:api/service/silobag/silobag-protocol';
import * as UI from '@siloreal:shared:ui';
import {
  analyzeSilobagsForClosure,
  type BlockedSilobag,
  type ClosureBlockReason,
} from '../extra/silobag-closure-analysis';
import * as HOOKS from '@siloreal:shared:hooks';

const SILOBAG_LIST_QUERY_KEY = ['silopro-vrr-list'] as const;
const PREVIEW_LIMIT = 100;

type Props = {
  open: boolean;
  silobagIds: readonly string[];
  onClose: () => void;
};

const WEIGHT_FORMATTER = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 2,
});

function normalizeIds(ids: readonly string[]): string[] {
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
}

function getErrorMessage(error: unknown): string {
  const i18n = HOOKS.useLocale.getState();
  if (!axios.isAxiosError(error))
    return i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_perform_single_closure');

  const responseData = error.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) return responseData;
  if (!responseData || typeof responseData !== 'object')
    return i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_perform_single_closure');

  const message = (responseData as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message;

  return i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_perform_single_closure');
}

function showClosureResult(response: CloseSilobagsResponse, requestedCount: number): void {
  const closedCount = response.filter((result) => result.status.toLowerCase() === 'closed').length;
  const failedCount = Math.max(requestedCount - closedCount, 0);
  const i18n = HOOKS.useLocale.getState();

  if (failedCount === 0) {
    toast.success(
      closedCount === 1
        ? i18n.t('temp.private.list.silospro.silopro_closure_modal.success')
        : `${i18n.t('temp.private.list.silospro.silopro_closure_modal.performed')} ${closedCount} ${i18n.t('temp.private.list.silospro.silopro_closure_modal.success_end')}`
    );
    return;
  }

  if (closedCount > 0) {
    toast.error(
      `${i18n.t('temp.private.list.silospro.silopro_closure_modal.performed')} ${closedCount} ${i18n.t('temp.private.list.silospro.silopro_closure_modal.closures_but')} ${failedCount} ${i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_be_closed')}`
    );
    return;
  }

  const firstError = response.find((result) => result.message)?.message;
  toast.error(
    firstError || i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_perform_closure')
  );
}

function WeightSummary({ silobag }: { silobag: SilobagListItem }) {
  const inputWeight = Number(silobag.input_weight);
  const extractedWeight = Number(silobag.total_extracted_weight);
  const difference = inputWeight - extractedWeight;
  const i18n = HOOKS.useLocale();

  const formatWeight = (value: number): string =>
    Number.isFinite(value) ? `${WEIGHT_FORMATTER.format(value)} kg` : i18n.t('temp.common.without_data');

  return (
    <Stack direction="row" flexWrap="wrap" gap={2}>
      <Typography variant="caption" color="text.secondary">
        {i18n.t('temp.common.bagging_weigth')}: <strong>{formatWeight(inputWeight)}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {i18n.t('temp.common.extracted_weight')}: <strong>{formatWeight(extractedWeight)}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {i18n.t('temp.common.difference')}: <strong>{formatWeight(difference)}</strong>
      </Typography>
    </Stack>
  );
}

function MonitoredSilobagIcon() {
  return (
    <Tooltip title="SiloPro con VRR">
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <VerifiedIcon sx={{ color: '#68BD00', fontSize: 16 }} />
      </span>
    </Tooltip>
  );
}

/*
function MonitoredSilobagsNotice({ silobags }: { silobags: readonly SilobagListItem[] }) {
  if (silobags.length === 0) return null;

  const visibleSilobags = silobags.slice(0, PREVIEW_LIMIT);
  const labels = visibleSilobags.map((silobag) => `${silobag.name} (${silobag.id})`).join(', ');
  const remainingCount = silobags.length - visibleSilobags.length;

  return (
    <Alert severity="warning" icon={<VerifiedIcon sx={{ color: '#68BD00' }} />}>
      {silobags.length === 1
        ? 'El siguiente SiloPro se encuentra bajo monitoreo '
        : 'Los siguientes SilosPro se encuentran bajo monitoreo '}
      <strong>{labels}</strong>
      {remainingCount > 0 ? ` y ${remainingCount} más` : ''}. En caso de continuar con la operación, la VRR
      pasará a estado NO OK, lo que afecta cualquier contrato (warrant, poliza de seguro, compra en origen,
      forward) vinculado al SiloPro.
      <strong> Esta acción es irreversible. ¿Desea continuar con la operación?</strong>
    </Alert>
  );
}
  */

function BlockedList({ silobags }: { silobags: readonly BlockedSilobag[] }) {
  const visibleSilobags = silobags.slice(0, PREVIEW_LIMIT);

  const i18n = HOOKS.useLocale();

  const REASON_LABEL: Record<ClosureBlockReason, string> = {
    closed: i18n.t('temp.private.list.silospro.silopro_closure_modal.closed_label'),
    'not-found': i18n.t('temp.private.list.silospro.silopro_closure_modal.not_found_label'),
    permission: i18n.t('temp.private.list.silospro.silopro_closure_modal.permission_label'),
    vrr: i18n.t('temp.private.list.silospro.silopro_closure_modal.vrr_label'),
  };

  return (
    <Stack gap={1}>
      {visibleSilobags.map((silobag) => (
        <Box
          key={silobag.id}
          sx={{
            border: '1px solid #F2CBCB',
            borderRadius: 2,
            backgroundColor: '#FFF8F8',
            px: 1.5,
            py: 1.25,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Stack minWidth={0}>
              <Typography fontWeight={700} color="#49454F" noWrap>
                {silobag.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {silobag.id}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" flexWrap="wrap" gap={0.75}>
              {silobag.reasons.map((reason) => (
                <Chip
                  key={reason}
                  label={REASON_LABEL[reason]}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      ))}
      {silobags.length > PREVIEW_LIMIT ? (
        <UI.Typography variant="system">
          {i18n.t('temp.private.list.silospro.silopro_closure_modal.and')} {silobags.length - PREVIEW_LIMIT}{' '}
          {i18n.t('temp.private.list.silospro.silopro_closure_modal.silobags_more')}.
        </UI.Typography>
      ) : null}
    </Stack>
  );
}

function ClosurePreview({
  silobags,
  monitoredSilobagIds,
}: {
  silobags: readonly SilobagListItem[];
  monitoredSilobagIds: ReadonlySet<string>;
}) {
  const visibleSilobags = silobags.slice(0, PREVIEW_LIMIT);
  const i18n = HOOKS.useLocale();

  return (
    <Stack gap={1}>
      {visibleSilobags.map((silobag) => {
        const isMonitored = monitoredSilobagIds.has(String(silobag.id));

        return (
          <Box
            key={silobag.id}
            sx={{
              border: '1px solid #DDE9CB',
              borderRadius: 2,
              backgroundColor: '#FBFDF8',
              px: 1.5,
              py: 1.25,
            }}
          >
            <Stack gap={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Stack direction="row" alignItems="center" minWidth={0} gap={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0, fontFamily: '"Roboto Mono", "SF Mono", monospace' }}
                  >
                    {silobag.id}
                  </Typography>
                  <Typography fontWeight={700} color="#49454F" noWrap>
                    {silobag.name}
                  </Typography>
                  {isMonitored ? <MonitoredSilobagIcon /> : null}
                </Stack>
                <Chip
                  label={i18n.t('temp.private.list.silospro.silopro_closure_modal.will_be_closed')}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              </Stack>
              <WeightSummary silobag={silobag} />
            </Stack>
          </Box>
        );
      })}
      {silobags.length > PREVIEW_LIMIT ? (
        <UI.Typography variant="system">
          {i18n.t('temp.private.list.silospro.silopro_closure_modal.and')} {silobags.length - PREVIEW_LIMIT}{' '}
          {i18n.t('temp.private.list.silospro.silopro_closure_modal.silobags_more')}.
        </UI.Typography>
      ) : null}
    </Stack>
  );
}

export function SilobagClosureModal({ open, silobagIds, onClose }: Props) {
  const queryClient = useQueryClient();
  const [blockedSelectionReviewed, setBlockedSelectionReviewed] = useState(false);
  const [isMonitoredWarningOpen, setIsMonitoredWarningOpen] = useState(false);
  const exactIdsKey = silobagIds.join('\u0000');
  const normalizedIds = useMemo(() => normalizeIds(exactIdsKey.split('\u0000')), [exactIdsKey]);
  const silobagsQuery = useQuery({
    queryKey: ['silobag-closure-preview', normalizedIds],
    queryFn: ({ signal }) =>
      listSilobagsService({
        exactIds: normalizedIds,
        pagination: { page: 0, take: normalizedIds.length },
        signal,
      }),
    enabled: open && normalizedIds.length > 0,
  });
  const monitoringQuery = useQuery({
    queryKey: ['silobag-closure-monitoring', normalizedIds],
    queryFn: ({ signal }) =>
      listMonitoredSilobagProtocolsService({
        silobagIds: normalizedIds,
        signal,
      }),
    enabled: open && normalizedIds.length > 0,
  });
  const closeMutation = useMutation({ mutationFn: closeSilobagsService });
  const analysis = useMemo(
    () => analyzeSilobagsForClosure(normalizedIds, silobagsQuery.data?.data ?? [], { blockVrr: false }),
    [normalizedIds, silobagsQuery.data?.data]
  );
  const monitoredSilobagIds = useMemo(
    () =>
      new Set(
        (monitoringQuery.data ?? [])
          .filter((silobag) => silobag.protocol != null)
          .map((silobag) => silobag.silobag_id)
      ),
    [monitoringQuery.data]
  );
  const monitoredClosableSilobags = useMemo(
    () => analysis.closable.filter((silobag) => monitoredSilobagIds.has(String(silobag.id))),
    [analysis.closable, monitoredSilobagIds]
  );
  const isLoading =
    silobagsQuery.isLoading ||
    silobagsQuery.isFetching ||
    monitoringQuery.isLoading ||
    monitoringQuery.isFetching;
  const isError = silobagsQuery.isError || monitoringQuery.isError;
  const showBlockedStep =
    silobagsQuery.isSuccess &&
    monitoringQuery.isSuccess &&
    analysis.blocked.length > 0 &&
    !blockedSelectionReviewed;
  const isBusy = closeMutation.isPending;

  const handleClose = (): void => {
    if (!isBusy && !isMonitoredWarningOpen) onClose();
  };

  const performClosure = async (acceptProtocolInvalidation: boolean): Promise<void> => {
    const idsToClose = analysis.closable.map((silobag) => silobag.id);
    if (idsToClose.length === 0) return;

    try {
      const response = await closeMutation.mutateAsync({
        silobagIds: idsToClose,
        acceptProtocolInvalidation,
      });
      onClose();
      showClosureResult(response, idsToClose.length);
      void queryClient.invalidateQueries({ queryKey: SILOBAG_LIST_QUERY_KEY });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleConfirm = (): void => {
    if (monitoredClosableSilobags.length > 0) {
      setIsMonitoredWarningOpen(true);
      return;
    }

    void performClosure(false);
  };

  const handleConfirmMonitoredClosure = (): void => {
    setIsMonitoredWarningOpen(false);
    void performClosure(true);
  };

  const handleRetry = (): void => {
    void Promise.all([silobagsQuery.refetch(), monitoringQuery.refetch()]);
  };

  const i18n = HOOKS.useLocale();

  const title = isLoading
    ? i18n.t('temp.private.list.silospro.silopro_closure_modal.validating_closure')
    : isError
      ? i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_validate_closure')
      : showBlockedStep
        ? i18n.t('temp.private.list.silospro.silopro_closure_modal.verify_selection')
        : analysis.closable.length === 1
          ? i18n.t('temp.private.list.silospro.silopro_closure_modal.accounting_closure')
          : `${i18n.t('temp.private.list.silospro.silopro_closure_modal.will_be_performed')} ${analysis.closable.length} ${i18n.t('temp.private.list.silospro.silopro_closure_modal.closures')}`;
  const description = isLoading
    ? i18n.t('temp.private.list.silospro.silopro_closure_modal.getting_selection_data')
    : isError
      ? i18n.t('temp.private.list.silospro.silopro_closure_modal.retry_verify')
      : showBlockedStep
        ? i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_be_performed')
        : i18n.t('temp.private.list.silospro.silopro_closure_modal.check_selection');

  let actions: ReactNode;
  if (isLoading) {
    actions = (
      <UI.Button variant="alternative-hollow" uppercase={false} onClick={handleClose}>
        {i18n.t('temp.common.cancel')}
      </UI.Button>
    );
  } else if (isError) {
    actions = (
      <>
        <UI.Button variant="alternative-hollow" uppercase={false} onClick={handleClose}>
          {i18n.t('temp.common.cancel')}
        </UI.Button>
        <UI.Button variant="primary" uppercase={false} onClick={handleRetry}>
          {i18n.t('temp.common.retry')}
        </UI.Button>
      </>
    );
  } else if (showBlockedStep) {
    actions = (
      <>
        <UI.Button variant="alternative-hollow" uppercase={false} onClick={handleClose}>
          {analysis.closable.length === 0 ? i18n.t('temp.common.cancel') : i18n.t('temp.common.cancel')}
        </UI.Button>
        {analysis.closable.length > 0 ? (
          <UI.Button
            variant="primary"
            uppercase={false}
            onClick={() => setBlockedSelectionReviewed(true)}
            icon={{ value: <TaskAltRoundedIcon fontSize="small" />, position: 'end' }}
            tid="silobag-closure-review-continue-btn"
          >
            {i18n.t('temp.private.list.silospro.silopro_closure_modal.continue_with')}{' '}
            {analysis.closable.length}
          </UI.Button>
        ) : null}
      </>
    );
  } else {
    actions = (
      <>
        <UI.Button
          variant="alternative-hollow"
          uppercase={false}
          disabled={isBusy}
          onClick={analysis.blocked.length > 0 ? () => setBlockedSelectionReviewed(false) : handleClose}
        >
          {analysis.blocked.length > 0 ? i18n.t('temp.common.return') : i18n.t('temp.common.cancel')}
        </UI.Button>
        <UI.Button
          variant="danger"
          uppercase={false}
          loading={isBusy}
          disabled={analysis.closable.length === 0}
          onClick={handleConfirm}
          icon={isBusy ? undefined : { value: <CancelIcon fontSize="small" />, position: 'end' }}
          tid="silobag-closure-confirm-btn"
        >
          {i18n.t('temp.private.list.silospro.silopro_closure_modal.confirm_closure')}
        </UI.Button>
      </>
    );
  }

  return (
    <>
      {isMonitoredWarningOpen ? (
        <MonitoredWarningExtractionModal
          open
          setOpen={setIsMonitoredWarningOpen}
          handleSave={handleConfirmMonitoredClosure}
          monitoredSilobagIds={monitoredClosableSilobags.map((silobag) => String(silobag.id))}
        />
      ) : null}

      <UI.Modal
        open={open}
        onClose={handleClose}
        title={title}
        actions={actions}
        size="md"
        fullWidth
        dividers
        disableBackdropClose={isBusy || isMonitoredWarningOpen}
        tid="silobag-closure-modal"
      >
        <Stack gap={2}>
          <UI.Typography variant="system">{description}</UI.Typography>
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" gap={1.5} py={4}>
              <CircularProgress size={32} />
              <UI.Typography variant="system">
                {i18n.t('temp.private.list.silospro.silopro_closure_modal.validating')}
              </UI.Typography>
            </Stack>
          ) : isError ? (
            <Alert severity="error">
              {i18n.t('temp.private.list.silospro.silopro_closure_modal.could_not_be_validated')}
            </Alert>
          ) : showBlockedStep ? (
            <Stack gap={2}>
              <Alert severity="warning" icon={<ErrorOutlineRoundedIcon />}>
                {analysis.blocked.length === 1
                  ? i18n.t('temp.private.list.silospro.silopro_closure_modal.one_left_out_of_closure')
                  : `${analysis.blocked.length} ${i18n.t('temp.private.list.silospro.silopro_closure_modal.left_out_of_closure')}`}
              </Alert>
              <BlockedList silobags={analysis.blocked} />
              {analysis.closable.length > 0 ? (
                <>
                  <Divider />
                  <Alert severity="success">
                    {i18n.t('temp.private.list.silospro.silopro_closure_modal.there_is')}{' '}
                    {analysis.closable.length}{' '}
                    {i18n.t('temp.private.list.silospro.silopro_closure_modal.available_to_close')}
                  </Alert>
                </>
              ) : (
                <Alert severity="info">
                  {i18n.t('temp.private.list.silospro.silopro_closure_modal.nothing_available_to_close')}
                </Alert>
              )}
            </Stack>
          ) : (
            <Stack gap={2}>
              <Alert severity="info">
                {analysis.closable.length === 1
                  ? i18n.t('temp.private.list.silospro.silopro_closure_modal.next_closure')
                  : `${i18n.t('temp.private.list.silospro.silopro_closure_modal.will_be_performed')} ${analysis.closable.length} ${i18n.t('temp.common.closures')}`}
              </Alert>

              <ClosurePreview silobags={analysis.closable} monitoredSilobagIds={monitoredSilobagIds} />
            </Stack>
          )}
        </Stack>
      </UI.Modal>
    </>
  );
}
